import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef, useCallback } from "react";
import dioroLogoAsset from "@/assets/dioro-logo-lockup.png.asset.json";


export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Salesforce Contract & Procurement Advisory | SaaSed" },
      { name: "description", content: "Salesforce procurement and commercial advisory for mid-to-large enterprises — data-driven contract alignment and negotiation support." },
      { property: "og:title", content: "Salesforce Contract & Procurement Advisory | SaaSed" },
      { property: "og:description", content: "Salesforce procurement and commercial advisory for mid-to-large enterprises — data-driven contract alignment and negotiation support." },
      { name: "twitter:title", content: "Salesforce Contract & Procurement Services | SaaSed" },
      { name: "twitter:description", content: "Salesforce procurement and commercial advisory for mid-to-large enterprises — data-driven contract alignment and negotiation support." },
      { property: "og:url", content: "https://saased.co/services" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://saased.co/services#faq",
          mainEntity: servicesFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ServicesPage,
});

type ServicesFaq = { q: string; a: string; links?: Record<string, string>; more?: { label: string; to: string } };

const servicesFaqs: ServicesFaq[] = [
  { q: "What is SaaSed?", a: "SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.", more: { label: "Read the full overview", to: "/salesforce-procurement" } },
  { q: "What does SaaSed's Salesforce advisory include?", a: "We run the renewal end to end. We review every Order Form line by line, including renewal dates, auto-renewal traps, true-up clauses and SKU-level pricing. We compare what you use with what you pay for, build a negotiation strategy with a target price and alternative options, and check that the deal you negotiated is the deal you sign." },
  { q: "When should we start a Salesforce renewal?", a: "Ideally 12 to 18 months before the contract end date. The earlier a company starts, the better the data quality and the better the leverage.", more: { label: "Read the full timeline", to: "/blog/when-does-your-salesforce-renewal-actually-start" } },
  { q: "Can SaaSed work alongside our internal team?", a: "Yes. We can act as your negotiation partner, or as a trusted advisor who coaches your team behind the scenes." },
  { q: "Does a Salesforce SELA or AELA make sense for us?", a: "It depends. Enterprise Licence Agreements can be powerful when adoption is high, usage is clear and the commercial model matches your roadmap. When they are agreed too early, too broadly or without enough visibility, they can become more expensive than a standard contract. We help assess whether a SELA or AELA makes sense, or whether another structure gives more flexibility and control.", links: { SELA: "/sela", AELA: "/aela" } },
  { q: "What is Dioro?", a: "Dioro is SaaSed's own Salesforce analytics tool. It delivers licence and SKU audits, usage insights, benchmarking and forecasting to support renewal and negotiation decisions.", links: { Dioro: "/dioro" } },
  { q: "Is SaaSed independent of Salesforce?", a: "SaaSed is independent of Salesforce: we are not a Salesforce partner or reseller. We work for the buyer, and we aim to deliver the outcome they hired us for while keeping their relationship with Salesforce intact." },
];

const faqLinkClass = "underline! decoration-[#f5cc00]! decoration-2! underline-offset-4";

function renderFaqAnswer(f: ServicesFaq) {
  if (!f.links) return f.a;
  const keys = Object.keys(f.links);
  const parts = f.a.split(new RegExp(`\\b(${keys.join("|")})\\b`));
  return parts.map((p, i) =>
    f.links![p] ? (
      <a key={i} href={f.links![p]} className={faqLinkClass}>{p}</a>
    ) : (
      p
    ),
  );
}

type DiagramStep = { title: string; desc: string };

type Service = {
  title: string;
  desc: string;
  details: {
    intro: string;
    bullets?: string[];
    diagram?: { heading: string; lead: string; steps: DiagramStep[] };
    outcome: string;
  };
};

const services: Service[] = [
  {
    title: "Full negotiation support",
    desc: "Whether as your negotiation partner or a trusted advisor coaching your team behind the scenes.",
    details: {
      intro:
        "We join your renewal cycle as your dedicated Salesforce negotiation partner — either openly across the table from the vendor, or quietly behind the scenes coaching your procurement and finance teams. Either way, you keep control; we bring the leverage, the pricing intelligence and the discipline of a team that does this every week.",
      diagram: {
        heading: "What we do",
        lead: "We run your Salesforce renewal end to end — from preparation to signed contract.",
        steps: [
          {
            title: "Start early",
            desc: "The earlier a company starts this process, the better the data quality and hence, better leverage.",
          },
          {
            title: "Thorough analysis of the contract",
            desc: "Every Order Form line by line: renewal dates, auto-renewal traps, true-up clauses, SKU-level pricing.",
          },
          {
            title: "Rightsize on real usage",
            desc: "Map what you actually use against what you pay for, minimise shelfware, apply appropriate tiers, and benchmark every SKU against similar contracts and profiles.",
          },
          {
            title: "Build the strategy and run the conversations",
            desc: "Target price, alternative options, levers in order — then prep you for every Salesforce meeting.",
          },
          {
            title: "Verify the win",
            desc: "Check the final contract, ensure the deal negotiated is the deal you sign, and confirm calculations.",
          },
        ],
      },
      outcome: "You sign a contract that reflects what you actually need — at a price that reflects your real buying power.",
    },
  },
  {
    title: "Software audit",
    desc: "A clear-eyed look at how you are utilizing your licenses in regard to presale ambition and post-sale delivery — so you stop paying for shelfware.",
    details: {
      intro:
        "Most Salesforce estates drift over time: editions get upgraded, add-ons get bundled in, headcount changes and nobody re-checks whether the contract still matches reality. Our audit compares what you bought to what you actually use, surface by surface, and translates the gap into concrete commercial actions before your next renewal.",
      diagram: {
        heading: "What we do",
        lead: "We measure what you actually use against what you bought — and turn the gap into commercial action.",
        steps: [
          {
            title: "License utilisation analysis",
            desc: "Per user, team and business unit — we map who is active, who is dormant, and where licenses are stranded.",
          },
          {
            title: "Surface the shelfware",
            desc: "Shelfware, duplicate functionality and over-provisioning surfaced with hard numbers, not opinions.",
          },
          {
            title: "Right-size editions and add-ons",
            desc: "Edition and add-on recommendations based on real usage profiles, not the original sales pitch.",
          },
          {
            title: "Map adoption vs. business case",
            desc: "Compare adoption gaps against the business case you were originally sold to expose unused value.",
          },
          {
            title: "Prioritised action list",
            desc: "A ranked list of what to keep, cut, downgrade or renegotiate before the next renewal cycle.",
          },
        ],
      },
      outcome: "A defensible inventory you can take straight into your next renewal conversation.",
    },
  },
  {
    title: "Contract SKU review",
    desc: "Have us review your contract product lines line by line and help you understand your real buying power. Reach out for a complimentary audit.",
    details: {
      intro:
        "Salesforce contracts are dense on purpose. Uplift clauses, bundled SKUs, ramped pricing and auto-renewal language all sit inside the order form and the MSA, and each one quietly shifts leverage toward the vendor. We read every line and translate it into a plain-English picture of where you stand and where you can push.",
      diagram: {
        heading: "What we do",
        lead: "We read every line of your contract and translate it into leverage you can actually use.",
        steps: [
          {
            title: "SKU-by-SKU analysis",
            desc: "Pricing, discount and ramp analysis across every product line on your Order Form.",
          },
          {
            title: "Flag the fine print",
            desc: "Uplift caps, auto-renewal and termination clauses pulled out and translated into plain English.",
          },
          {
            title: "Bundle vs. unbundled",
            desc: "Cost comparison so you see what you are really paying for each product, not just the bundle total.",
          },
          {
            title: "Benchmark the deal",
            desc: "Comparison against comparable EMEA deals of similar size and edition to size your real buying power.",
          },
          {
            title: "Negotiation playbook",
            desc: "A playbook tailored to the exact contract on your desk — which levers exist and which to pull first.",
          },
        ],
      },
      outcome: "You walk into your next renewal knowing exactly which levers exist and which ones to pull first.",
    },
  },
];

function ServicesPage() {


  return (
    <>
    <section className="container-x pt-10 md:pt-12">
      <div className="flex flex-col items-center text-center">
        <h1 className="m-0 mx-auto max-w-5xl text-4xl leading-[1.05] md:text-5xl">
          We run your Salesforce renewal end to end
        </h1>
      </div>

      {/* Featured service with interactive diagram */}
      {services
        .filter((s) => s.details.diagram)
        .slice(0, 1)
        .map((s) => (
          <InteractiveDiagram key={s.title} service={s} />
        ))}



      <ServicesList />

      <div className="mt-16! md:mt-20!">
        <h2 className="mb-10! font-serif text-4xl font-medium tracking-tight text-foreground md:text-5xl lg:text-6xl">
          Our Products
        </h2>
      </div>

      <DioroSection />

      <SelaSection />
    </section>

      {/* FAQ */}
      <section className="bg-[#f0e8c8]">
        <div className="container-x py-16 md:py-20">
          <h2 className="m-0 text-center font-serif text-3xl font-bold text-[#1a1408] md:text-4xl">
            FAQ about Salesforce Advisory
          </h2>
          <div aria-hidden="true" className="mx-auto mt-4 h-[2px] w-16 bg-[#f5cc00]" />
          <div className="mx-auto mt-9! flex max-w-3xl flex-col gap-4">
            {servicesFaqs.map((f, i) => (
              <details key={f.q} open={i === 0} className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                  <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">{f.q}</h3>
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
                </summary>
                <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">{renderFaqAnswer(f)}</p>
                  {f.more && (
                    <p className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">
                      <Link to={f.more.to} className={faqLinkClass}>{f.more.label}</Link>
                    </p>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

type ServiceItem = { title: string; description: string };

const serviceItems: ServiceItem[] = [
  {
    title: "Contract & SKU Review",
    description:
      "Line-by-line review of your Order Form and MSA. We translate uplift caps, ramps, bundles and auto-renewal clauses into plain English and benchmark every SKU against comparable EMEA deals — so you know exactly where the leverage sits.",
  },
  {
    title: "Software Audit",
    description:
      "A clear-eyed view of how your licenses are actually used versus what you bought. We surface shelfware, duplicate functionality and over-provisioning with hard numbers, then turn the gap into a prioritised action list for your next renewal.",
  },
  {
    title: "Full Negotiation Support",
    description:
      "We run your Salesforce renewal end to end — across the table as your negotiation partner, or quietly coaching your team behind the scenes. You keep control; we bring the pricing intelligence, leverage and discipline of a team that does this every week.",
  },
];

function ServicesList() {
  const [active, setActive] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [displayed, setDisplayed] = useState(serviceItems[0]);
  const current = serviceItems[active];

  useEffect(() => {
    if (current.description === displayed.description) return;
    setIsFading(true);
    const t1 = window.setTimeout(() => {
      setDisplayed(current);
      setIsFading(false);
    }, 200);
    return () => window.clearTimeout(t1);
  }, [current, displayed.description]);

  return (
    <div className="mt-16! md:mt-20!">
      <h2 className="mb-10! font-serif text-4xl font-medium tracking-tight text-foreground md:text-5xl lg:text-6xl">
        Our Services
      </h2>
      <div className="grid gap-10 md:grid-cols-12 md:gap-12">
        <ol className="md:col-span-5" role="tablist" aria-label="Service categories">
          {serviceItems.map((item, i) => {
            const isActive = i === active;
            return (
              <li key={item.title} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="services-panel"
                  id={`services-tab-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`group flex w-full items-center gap-4 py-5 text-left transition-colors ${
                    isActive ? "text-foreground" : "text-foreground/60 hover:text-foreground"
                  }`}
                >
                  <span className={`font-sans text-xs tabular-nums tracking-[0.2em] ${isActive ? "text-foreground" : "text-foreground/40"}`}>
                    0{i + 1}
                  </span>
                  <span className="text-xl md:text-2xl">{item.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <div
          id="services-panel"
          role="tabpanel"
          aria-labelledby={`services-tab-${active}`}
          className="md:col-span-7 md:pl-8 md:pt-5 max-md:h-[220px] max-md:overflow-y-auto"
        >
          {/* Desktop: all three descriptions stay mounted; CSS-only show/hide */}
          {serviceItems.map((item, i) => (
            <div
              key={item.title}
              className={i === active ? "hidden animate-fade-in md:block" : "hidden"}
            >
              <p className="text-base leading-relaxed text-muted-foreground">
                {item.description}
              </p>
            </div>
          ))}
          {/* Mobile: locked-height container + compositor-only opacity/transform transition.
              All three descriptions stay mounted; inactive ones are clipped and out of flow. */}
          <div className="relative h-full md:hidden">
            {serviceItems.map((item) => {
              const isDisplayed = item.description === displayed.description;
              return (
                <div
                  key={item.title}
                  aria-hidden={!isDisplayed}
                  className={
                    isDisplayed
                      ? `transition-opacity duration-200 ease-in-out ${
                          isFading ? "opacity-0" : "opacity-100"
                        }`
                      : "absolute inset-0 overflow-hidden opacity-0 pointer-events-none"
                  }
                >
                  <p className="text-base leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function DioroSection() {
  return (
    <section
      aria-labelledby="dioro-heading"
      className="-mx-6 md:-mx-10 w-[calc(100%+3rem)] md:w-[calc(100%+5rem)]"
      style={{ background: "linear-gradient(115deg, #FFFDF0 0%, #FFFDF0 42%, #EAF3C4 68%, #D9EC9C 100%)" }}
    >
      <div className="container-x" style={{ paddingTop: "64px", paddingBottom: "64px", paddingLeft: "56px", paddingRight: "56px" }}>
        {/* Logo lockup */}
        <img
          src={dioroLogoAsset.url}
          alt="Dioro by SaaSed"
          width="150"
          height="50"
          className="h-auto w-[150px]"
        />


        {/* Paragraphs */}
        <div className="mt-6 flex flex-col gap-4" style={{ maxWidth: "760px" }}>
          <p
            style={{
              fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: "15.5px",
              lineHeight: 1.65,
              color: "#3D3928",
            }}
          >
            Dioro is part of how we work. It goes through every SKU in your Salesforce org to identify exactly what license type and edition you're on, and keeps that current as Salesforce renames and reshuffles its products from underneath you.
          </p>
          <p
            style={{
              fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: "15.5px",
              lineHeight: 1.65,
              color: "#3D3928",
            }}
          >
            Two things fall out of that: where you're paying for seats and features nobody's using, and whether you're even on the right licenses for how your teams work. What we do with that intelligence is the rest of the job — we turn those findings into leverage, building the strategy you need going into your next negotiation.
          </p>
        </div>

        {/* Link */}
        <a
          href="/dioro"
          className="mt-8 inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
          style={{
            fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
            fontWeight: 700,
            fontSize: "15px",
            color: "#1A1408",
          }}
          aria-label="Read more about Dioro"
        >
          <span>Read more about Dioro</span>
          <span style={{ color: "#9DC800" }} aria-hidden="true">&rarr;</span>
        </a>
      </div>
    </section>
  );
}

function SelaSection() {
  return (
    <div className="bg-[#fffdf0] py-16 md:py-20">
      <div className="container-x">
        <h2 className="mb-10! w-full text-left text-4xl font-bold text-[#1a1408] lg:text-5xl">
          Expertise in SELA & AELA
        </h2>
        <div className="relative grid grid-cols-1 items-start gap-8 md:grid-cols-2 lg:gap-12">
          {/* LEFT COLUMN — SELA */}
          <div className="flex flex-col rounded-2xl border border-[#f0e8c8] bg-brand-cream p-8 font-sans">
            <h3 className="m-0 text-left font-serif text-2xl font-bold text-brand-ink">
              Salesforce SELA Advisory
            </h3>
            <p className="m-0 mt-4! text-base leading-[1.6] text-brand-ink/90 md:min-h-[6.4em] lg:min-h-[4.8em]">
              Salesforce Enterprise Licence Agreements can be powerful when adoption is high, usage is clear, and the commercial model matches your roadmap.
            </p>
            <details className="group mt-5!">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl py-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-ink [&::-webkit-details-marker]:hidden">
                <span className="text-lg font-medium leading-[1.35]">What we cover</span>
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-sand transition-colors duration-200 group-open:bg-brand-punch">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-brand-ink transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </span>
              </summary>
              <div className="flex flex-col gap-4 pt-4 text-base leading-[1.6]">
            <p className="m-0 text-base leading-[1.6] text-brand-ink/90">
              We help organisations assess whether a SELA genuinely makes sense for their business, or whether a different structure would give them more flexibility, control, and long-term value.
            </p>
            <div className="flex flex-col gap-[10px]">
              <p className="m-0 text-base font-medium text-brand-ink/90">
                Our work typically focuses on:
              </p>
              <ul className="m-0 flex flex-col gap-2 text-base text-brand-ink/80">
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Understanding current and expected product adoption</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Reviewing usage against contracted capacity</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Identifying shelfware and overcommitment risk</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Assessing whether the commercial structure matches the roadmap</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Supporting SELA renewal, restructuring, or unbundling conversations</span>
                </li>
              </ul>
            </div>
            <p className="m-0 text-base leading-[1.6] text-brand-ink/90">
              The goal is not simply to negotiate a better agreement.
            </p>
            <p className="m-0 text-base leading-[1.6] text-brand-ink/90">
              It is to make sure the agreement fits how your organisation actually uses Salesforce for now and in the future.
            </p>
              </div>
            </details>
            <Link
              to="/sela"
              className="mt-4! inline-block self-start text-base font-bold text-brand-ink underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 transition-colors hover:opacity-90"
              aria-label="Read more about Salesforce SELA Contracts"
            >
              Read more about Salesforce SELA Contracts -&gt;
            </Link>
          </div>

          {/* RIGHT COLUMN — AELA & Agentforce */}
          <div className="flex flex-col rounded-2xl border border-[#f0e8c8] bg-brand-cream p-8 font-sans">
            <h3 className="m-0 text-left font-serif text-2xl font-bold text-brand-ink">
              Agentforce & AELA Advisory
            </h3>
            <p className="m-0 mt-4! text-base leading-[1.6] text-brand-ink/90 md:min-h-[6.4em] lg:min-h-[4.8em]">
              Without the right guardrails, organisations can commit to rate cards and consumption pools that look attractive in the proposal but scale faster than the business case allows.
            </p>
            <details className="group mt-5!">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl py-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-ink [&::-webkit-details-marker]:hidden">
                <span className="text-lg font-medium leading-[1.35]">What we cover</span>
                <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-sand transition-colors duration-200 group-open:bg-brand-punch">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-brand-ink transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </span>
              </summary>
              <div className="flex flex-col gap-4 pt-4 text-base leading-[1.6]">
            <p className="m-0 text-base leading-[1.6] text-brand-ink/90">
              We help procurement and finance teams evaluate whether an AELA structure fits their AI roadmap, and if so, how to contract it without surrendering long-term leverage.
            </p>
            <div className="flex flex-col gap-[10px]">
              <p className="m-0 text-base font-medium text-brand-ink/90">
                Our AELA &amp; Agentforce work covers:
              </p>
              <ul className="m-0 flex flex-col gap-2 text-base text-brand-ink/80">
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Auditing current and projected flex credit consumption</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Mapping token usage against contracted entitlement</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Benchmarking Agentforce rate cards and overage terms</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Modelling SELA vs. AELA financial scenarios</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-ink">•</span>
                  <span>Negotiating consumption caps, true-up protections and governance rights</span>
                </li>
              </ul>
            </div>
            <p className="m-0 text-base leading-[1.6] text-brand-ink/90">
              The objective is the same: a contract structure that reflects real usage, protects budget predictability, and preserves your negotiating position at renewal.
            </p>
              </div>
            </details>
            <Link
              to="/aela"
              className="mt-4! inline-block self-start text-base font-bold text-brand-ink underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 transition-colors hover:opacity-90"
              aria-label="Read more about Salesforce AELA and Agentforce Advisory"
            >
              Read more about Agentforce &amp; AELA Contracts -&gt;
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}



function InteractiveDiagram({ service }: { service: Service }) {
  const steps = service.details.diagram!.steps;
  const [active, setActive] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const palette = [
    "bg-amber-50",
    "bg-amber-100",
    "bg-amber-200",
    "bg-yellow-300",
    "bg-yellow-400",
  ];

  const lineShades = [
    "bg-amber-100",
    "bg-amber-200",
    "bg-yellow-300",
    "bg-yellow-400",
    "bg-yellow-500",
  ];
  const ringShades = [
    "ring-amber-100/30",
    "ring-amber-200/20",
    "ring-amber-300/20",
    "ring-yellow-300/20",
    "ring-yellow-400/20",
  ];

  const startAuto = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % steps.length);
    }, 2000);
  }, [steps.length]);

  const stopAuto = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => {
    startAuto();
    return () => stopAuto();
  }, [startAuto, stopAuto]);

  const handleActivate = (i: number) => {
    setActive(i);
    startAuto();
  };

  return (
    <div className="mt-10">
      {/* Horizontal 1→5 step diagram acting as tabs */}
      <ol className="grid gap-4 md:grid-cols-5 md:items-stretch">
        {steps.map((step, i) => {
          const isActive = i === active;
          return (
            <li key={step.title} className="flex h-full">
              <button
                type="button"
                onMouseEnter={() => handleActivate(i)}
                onFocus={() => handleActivate(i)}
                onClick={() => handleActivate(i)}
                className={`group relative flex h-full w-full flex-col gap-4 rounded-2xl border p-6 text-left transition-all duration-300 ease-in-out ${
                  isActive
                    ? "border-foreground bg-card shadow-soft"
                    : "border-border bg-card/50 hover:border-foreground/40 hover:bg-card"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ring-4 transition-transform ${palette[i]} ${ringShades[i]} text-stone-950 ${
                      isActive ? "scale-110" : ""
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={`hidden h-0.5 flex-1 md:block ${lineShades[i]}`}
                    aria-hidden
                  />
                </div>
                <p
                  className={`m-0 text-sm font-semibold transition-colors ${
                    isActive ? "text-foreground" : "text-foreground/80"
                  }`}
                >
                  {step.title}
                </p>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isActive ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 md:max-h-[500px]"
                  }`}
                  aria-hidden={!isActive}
                >
                  <p className="m-0 text-sm leading-relaxed text-muted-foreground">
                    {step.desc}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      <p className="mt-10! mb-0! py-0 text-center text-sm font-medium italic text-foreground">
        {service.details.outcome}
      </p>
    </div>
  );
}
